/**
 * Scene Graph — Core 1 of the NSPP runtime.
 *
 * Node types: rect (solid color), text (canvas-baked glyphs in a quad),
 * image (uploaded image in a quad). All share the same SDF rect pipeline.
 *
 * Previous-frame transforms are stored so we can derive velocity for motion blur.
 *
 * Layer 3 extension: per-corner radii, padding, per-side borders, flex-derived
 * computed size, transform origin, visibility, pointer-events, blend mode,
 * overflow clip, filter matrix, className for state-driven pseudo-class
 * resolution, keyframe animation track.
 */

export type NodeType = "rect" | "text" | "image";

export type NodeId = number;

export interface RGBA {
  r: number; // 0..1
  g: number;
  b: number;
  a: number;
}

/** Corner radii in CSS order: TL, TR, BR, BL. */
export type CornerRadii = [number, number, number, number];

/** Per-side border widths: top, right, bottom, left. */
export type BorderWidths = [number, number, number, number];

/**
 * Filter chain. Applied in order in the fragment shader (cheap variants).
 * Blur is approximated by a small fwidth-based soften, not Gaussian.
 *
 * `backdropBlur` is the radius (CSS px) of the backdrop-filter blur — the
 * fragment shader samples the scene FBO behind this node and blurs it
 * before compositing. 0 (or omitted) disables it. Distinct from `blur`,
 * which softens the node's own edges.
 */
export interface FilterChain {
  blur: number;       // px
  brightness: number; // 1 = no change
  contrast: number;   // 1 = no change
  saturate: number;   // 1 = no change
  hueRotate: number;  // radians
  /** Optional inversion for dark-mode UIs. */
  invert: number;     // 0 or 1
  /** Backdrop blur radius in CSS px (0 = none). From CSS `backdrop-filter`. */
  backdropBlur?: number;
}

/** A single keyframe definition. Times are seconds from animation start. */
export interface KeyframeDef {
  /** Seconds. */
  at: number;
  /** Properties to interpolate at this keyframe. */
  props: Partial<AnimatableProps>;
  /** Easing applied between the PREVIOUS keyframe and this one. */
  easing?: string;
}

/** Properties that can be animated by keyframes. */
export interface AnimatableProps {
  x: number;
  y: number;
  opacity: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  fillR: number;
  fillG: number;
  fillB: number;
  fillA: number;
  radius: number;
}

export interface SceneNode {
  id: NodeId;
  type: NodeType;
  zIndex: number;
  /** Stacking context flag — nodes inside a transformed/opacity-parent
   *  are isolated from siblings. */
  stackingContext: boolean;

  // layout (final computed values)
  x: number;
  y: number;
  w: number;
  h: number;
  /** Per-corner radii [TL, TR, BR, BL]. */
  radii: CornerRadii;

  // box model (CSS-derived; not all used by the SDF kernel)
  padding: BorderWidths;

  // visual
  fill: RGBA;
  /** Border widths per side. 0 = no border on that side. */
  borderWidths: BorderWidths;
  /** Single border color for now (per-side borders can be added later). */
  borderColor: RGBA;
  /** Outer stroke for the SDF pipeline (single width + color, for back-compat). */
  strokeWidth: number;
  stroke: RGBA;

  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowBlur: number;
  shadowSpread: number;
  shadowColor: RGBA;

  // transform
  rotation: number;
  scaleX: number;
  scaleY: number;
  /** 0..1 in each axis. Default 0.5,0.5 (center). */
  transformOriginX: number;
  transformOriginY: number;

  // compositing
  opacity: number;
  /** "normal" | "multiply" | "screen" | "overlay" | "darken" | "lighten". */
  mixBlendMode: string;
  visibility: "visible" | "hidden";
  pointerEvents: "auto" | "none";
  /** If "hidden", children are clipped to this rect (post-transform clip). */
  overflow: "visible" | "hidden" | "scroll";

  // filter
  filter: FilterChain;

  // text-only
  text?: string;
  fontSize?: number;
  fontWeight?: number;
  fontFamily?: string;
  textColor?: RGBA;
  /** Multi-line wrap (0 = nowrap). Default 0; CSS white-space:normal => wrap. */
  textWrap?: boolean;
  /** Line-height multiplier (1.2 default). */
  lineHeight?: number;
  letterSpacing?: number;
  textAlign?: "left" | "center" | "right";
  /** If set, append "…" when wrapped text overflows. */
  textOverflowEllipsis?: boolean;

  // image-only
  imageUrl?: string;

  // svg path-only — raw `d` attribute. When present, the rect pipeline
  // rasterizes the path to a texture and uses the existing fillTex sampling
  // path (same as text and image nodes).
  pathD?: string;
  /** Optional viewBox override; defaults to node.w/h if absent. */
  pathViewBox?: { x: number; y: number; w: number; h: number };

  // gradient-only
  gradientEnd?: RGBA;
  gradientAngle?: number;

  // SDF library — when sdfKind is set, the rect pipeline replaces the
  // per-corner rounded-rect SDF with the named shape from src/gl/rectPipeline.ts.
  // sdfParamsA and sdfParamsB together carry up to 8 floats of parameters
  // (r, n, thickness, halfWidth, halfHeight, ...).
  sdfKind?: "rect" | "circle" | "roundedBox" | "ring" | "capsule" |
    "polygon" | "star" | "heart" | "arc" | "segment" | "bezier" |
    "rhombus" | "trapezoid" | "pie" | "horseshoe" | "orientedBox" |
    "equilateralTri" | "vesica" | "moon" | "ellipse" | "cross" |
    "parallelogram" | "hexagram" | "bobblyCross" | "egg" | "parabolaSeg" |
    "stairs" | "circleWave" | "hyperbola" | "coolS" | "pentagram" |
    "regularStar" | "isoscelesTri" | "roundedX" | "circleCross" |
    "orientedVesica" | "parabola" | "quadraticCircle" |
    "squircle" | "gear" | "spike" | "plus" |
    "cloud" | "lightning" | "speechBubble" | "heartBroken" | "dumbbell" |
    "flower" | "shield" | "bookmark" | "arrow" | "cog" | "tapeStrip" |
    "asterisk" | "spiral";
  /** First 4 floats fed to the named SDF. Meaning is shape-specific. */
  sdfParamsA?: [number, number, number, number];
  /** Optional 2nd set of 4 floats. Use for shapes with > 4 params. */
  sdfParamsB?: [number, number, number, number];

  // CSS state + identity
  /** Element tag, e.g. "div", "button". Used by hover/focus state resolution. */
  tagName?: string;
  /** class attribute (space-separated). Used to match :hover and other
   *  pseudo-classes against style rules. */
  className?: string;
  /** Inline style attribute raw text — preserved for debugging. */
  styleAttr?: string;

  // animation (CSS-style)
  /** Active keyframe timeline; if present, the engine drives x/y/opacity/etc
   *  from this rather than the update() callback. */
  animation?: KeyframeTrack;

  // velocity sources (set by engine)
  prevX: number;
  prevY: number;
  prevRotation: number;
  lastT: number;

  // free-form callback for non-CSS updates (drag inertia uses node.x/y
  // directly; this is for ad-hoc JS-driven motion like Scene E)
  update?: (node: SceneNode, t: number, dt: number) => void;
  /** If set, a tap on this node (pointerdown+up within 8px / 500ms) calls
   *  switchScene(engine, this value). Used for the landing's dock icons
   *  to jump straight into a specific scene. */
  clickScene?: string;
  /** Generic tap handler. Fires on pointerup that started within 8px /
   *  500ms of the down event. Use this for in-scene interactions like
   *  "click this thumbnail to swap the main image". */
  onTap?: () => void;
}

export interface KeyframeTrack {
  /** Total duration in seconds; animation loops if iterations === "infinite". */
  duration: number;
  /** Seconds to wait before first frame. */
  delay: number;
  /** Number of iterations or "infinite". */
  iterations: number | "infinite";
  /** "normal" | "reverse" | "alternate" | "alternate-reverse". */
  direction: string;
  /** "none" | "forwards" | "backwards" | "both". */
  fillMode: string;
  /** Current iteration counter (set by engine). */
  currentIteration: number;
  /** Elapsed time within the current iteration (set by engine). */
  elapsedInIteration: number;
  /** Keyframes. */
  keyframes: KeyframeDef[];
  /** Easing for the whole animation if not specified per-keyframe. */
  defaultEasing?: string;
  /** Whether to pause (set true when animation-pending). */
  paused: boolean;
  /**
   * When true, progress is driven by the page's scroll position instead of
   * frame dt. The engine reads scrollY, normalizes by `scrollRange` (or
   * document scrollHeight if unset), and sets elapsedInIteration from that.
   * See src/scroll.ts. Default undefined → time-driven (legacy behavior).
   */
  scrollTrack?: boolean;
  /**
   * Pixel offsets [start, end] within the scroll source that map to progress
   * [0, 1]. Defaults to [0, document.scrollHeight - viewportHeight] when
   * the scroll source is the window. Negative offsets are allowed and mean
   * "trigger the animation before the user has scrolled to the top".
   */
  scrollRange?: [number, number];
}

export interface Scene {
  nodes: Map<NodeId, SceneNode>;
  insertion: NodeId[];
  sorted: SceneNode[] | null;
  version: number;
  /** Per-element CSS class rules, indexed by tagName+className.
   *  Filled in by cssIngest. The engine resolves :hover/:focus by walking
   *  this map and rebuilding the node's style on state change. */
  styleRules: Map<string, Partial<ComputedStyle>>;
  /** Currently focused node id (for :focus state). */
  focusedNode: NodeId | null;
  /** Currently hovered node id (for :hover state). */
  hoveredNode: NodeId | null;
  /** Optional per-scene pointer-down handler. Receives (x, y) in CSS px.
   *  Fires on every pointer down BEFORE the Studio hit-test. Used for
   *  scenes that want to interpret clicks themselves (e.g. the product
   *  page's thumbnail picker). Return `true` to swallow the event so the
   *  Studio handler doesn't also fire. */
  onPointerDown?: (x: number, y: number) => boolean | void;
  /** Per-scene frame-rate-independent update hook. Called once per frame
   *  with the current scene time in seconds. Use this for timed animations
   *  that aren't tied to a specific node (e.g. background shimmer). */
  onTick?: (t: number, dt: number) => void;
}

/** Computed style — everything the SDF shader needs to know. */
export interface ComputedStyle {
  fill: RGBA;
  radii: CornerRadii;
  borderWidths: BorderWidths;
  borderColor: RGBA;
  strokeWidth: number;
  stroke: RGBA;
  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowBlur: number;
  shadowSpread: number;
  shadowColor: RGBA;
  opacity: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  transformOriginX: number;
  transformOriginY: number;
  mixBlendMode: string;
  visibility: "visible" | "hidden";
  pointerEvents: "auto" | "none";
  overflow: "visible" | "hidden" | "scroll";
  filter: FilterChain;
  // text-only
  fontSize: number;
  fontWeight: number;
  fontFamily: string;
  textColor: RGBA;
  textWrap: boolean;
  lineHeight: number;
  letterSpacing: number;
  textAlign: "left" | "center" | "right";
  textOverflowEllipsis: boolean;
  // image-only — set by <img src="..."> or background-image: url(...). When
  // present, the rect pipeline samples a texture at this URL instead of the
  // solid fill.
  imageUrl?: string;
  // flex
  display: string;
  flexDirection: string;
  justifyContent: string;
  alignItems: string;
  flexWrap: string;
  alignSelf: string;
  flexGrow: number;
  flexShrink: number;
  flexBasis: string;
  gap: number;
  // gradient fill (optional)
  gradientEnd?: RGBA;
  gradientAngle?: number;
  // CSS Grid track templates (empty = auto-fill)
  gridTemplateColumns: string;
  gridTemplateRows: string;
}

export function createScene(): Scene {
  return {
    nodes: new Map(),
    insertion: [],
    sorted: null,
    version: 0,
    styleRules: new Map(),
    focusedNode: null,
    hoveredNode: null,
  };
}

let _nextId = 1;
export function allocId(): NodeId {
  return _nextId++;
}

function initPrev(node: SceneNode): void {
  node.prevX = node.x;
  node.prevY = node.y;
  node.prevRotation = node.rotation;
  node.lastT = performance.now();
}

/** Helper to build a corner-radii tuple with a single value for all corners. */
export function radiiAll(v: number): CornerRadii {
  return [v, v, v, v];
}

function defaults(partial: Partial<SceneNode>): SceneNode["filter"] {
  return partial.filter ?? {
    blur: 0,
    brightness: 1,
    contrast: 1,
    saturate: 1,
    hueRotate: 0,
    invert: 0,
    backdropBlur: 0,
  };
}

export function makeRect(
  partial: Partial<SceneNode> & {
    x: number; y: number; w: number; h: number;
    /** Legacy single-corner radius. Sets all four corners. */
    radius?: number;
    /** Optional explicit filter chain (overrides computed defaults). */
    filter?: SceneNode["filter"];
    /** Backdrop blur radius in CSS px. 0 = none. Layer 5. */
    backdropBlur?: number;
  },
): SceneNode {
  const id = partial.id ?? allocId();
  const radii = partial.radii ?? radiiAll(partial.radius ?? 0);
  const node: SceneNode = {
    id,
    type: "rect",
    zIndex: partial.zIndex ?? 0,
    stackingContext: partial.stackingContext ?? false,
    x: partial.x, y: partial.y, w: partial.w, h: partial.h,
    radii,
    padding: partial.padding ?? [0, 0, 0, 0],
    fill: partial.fill ?? { r: 1, g: 1, b: 1, a: 1 },
    gradientEnd: partial.gradientEnd,
    gradientAngle: partial.gradientAngle,
    borderWidths: partial.borderWidths ?? [0, 0, 0, 0],
    borderColor: partial.borderColor ?? { r: 0, g: 0, b: 0, a: 1 },
    strokeWidth: partial.strokeWidth ?? 0,
    stroke: partial.stroke ?? { r: 0, g: 0, b: 0, a: 1 },
    shadowOffsetX: partial.shadowOffsetX ?? 0,
    shadowOffsetY: partial.shadowOffsetY ?? 0,
    shadowBlur: partial.shadowBlur ?? 0,
    shadowSpread: partial.shadowSpread ?? 0,
    shadowColor: partial.shadowColor ?? { r: 0, g: 0, b: 0, a: 0.5 },
    rotation: partial.rotation ?? 0,
    scaleX: partial.scaleX ?? 1,
    scaleY: partial.scaleY ?? 1,
    transformOriginX: partial.transformOriginX ?? 0.5,
    transformOriginY: partial.transformOriginY ?? 0.5,
    opacity: partial.opacity ?? 1,
    mixBlendMode: partial.mixBlendMode ?? "normal",
    visibility: partial.visibility ?? "visible",
    pointerEvents: partial.pointerEvents ?? "auto",
    overflow: partial.overflow ?? "visible",
    filter: defaults(partial),
    update: partial.update,
    tagName: partial.tagName,
    className: partial.className,
    styleAttr: partial.styleAttr,
    animation: partial.animation,
    clickScene: partial.clickScene,
    // Image + path fields. makeRect is the path-bearing node type —
    // when pathD is present the engine rasterizes it via bakePathToCanvas.
    pathD: partial.pathD,
    pathViewBox: partial.pathViewBox,
    prevX: 0, prevY: 0, prevRotation: 0, lastT: 0,
  };
  initPrev(node);
  return node;
}

/**
 * makeSdf — build a SceneNode whose silhouette is one of the Inigo Quilez
 * 2D SDFs. The rect pipeline detects `sdfKind` and replaces `sdPerCornerRect`
 * with the matching function in the fragment shader.
 */
export function makeSdf(
  partial: Partial<SceneNode> & {
    x: number; y: number; w: number; h: number;
    sdfKind: NonNullable<SceneNode["sdfKind"]>;
    sdfParamsA?: [number, number, number, number];
    sdfParamsB?: [number, number, number, number];
  },
): SceneNode {
  const node = makeRect(partial);
  node.sdfKind = partial.sdfKind;
  node.sdfParamsA = partial.sdfParamsA;
  node.sdfParamsB = partial.sdfParamsB;
  return node;
}

export function makeText(
  partial: Partial<SceneNode> & {
    x: number; y: number; w: number; h: number; text: string;
    radius?: number;
  },
): SceneNode {
  const id = partial.id ?? allocId();
  const radii = partial.radii ?? radiiAll(partial.radius ?? 0);
  const node: SceneNode = {
    id,
    type: "text",
    zIndex: partial.zIndex ?? 0,
    stackingContext: partial.stackingContext ?? false,
    x: partial.x, y: partial.y, w: partial.w, h: partial.h,
    radii,
    padding: partial.padding ?? [0, 0, 0, 0],
    fill: partial.fill ?? { r: 1, g: 1, b: 1, a: 1 },
    textColor: partial.textColor ?? partial.fill ?? { r: 1, g: 1, b: 1, a: 1 },
    text: partial.text,
    fontSize: partial.fontSize ?? 16,
    fontWeight: partial.fontWeight ?? 400,
    fontFamily: partial.fontFamily,
    textWrap: partial.textWrap ?? false,
    lineHeight: partial.lineHeight ?? 1.2,
    letterSpacing: partial.letterSpacing ?? 0,
    textAlign: partial.textAlign ?? "left",
    textOverflowEllipsis: partial.textOverflowEllipsis ?? false,
    borderWidths: partial.borderWidths ?? [0, 0, 0, 0],
    borderColor: partial.borderColor ?? { r: 0, g: 0, b: 0, a: 1 },
    strokeWidth: partial.strokeWidth ?? 0,
    stroke: partial.stroke ?? { r: 0, g: 0, b: 0, a: 1 },
    shadowOffsetX: partial.shadowOffsetX ?? 0,
    shadowOffsetY: partial.shadowOffsetY ?? 0,
    shadowBlur: partial.shadowBlur ?? 0,
    shadowSpread: partial.shadowSpread ?? 0,
    shadowColor: partial.shadowColor ?? { r: 0, g: 0, b: 0, a: 0.5 },
    rotation: partial.rotation ?? 0,
    scaleX: partial.scaleX ?? 1,
    scaleY: partial.scaleY ?? 1,
    transformOriginX: partial.transformOriginX ?? 0.5,
    transformOriginY: partial.transformOriginY ?? 0.5,
    opacity: partial.opacity ?? 1,
    mixBlendMode: partial.mixBlendMode ?? "normal",
    visibility: partial.visibility ?? "visible",
    pointerEvents: partial.pointerEvents ?? "auto",
    overflow: partial.overflow ?? "visible",
    filter: defaults(partial),
    update: partial.update,
    tagName: partial.tagName,
    className: partial.className,
    styleAttr: partial.styleAttr,
    animation: partial.animation,
    clickScene: partial.clickScene,
    // Image + path fields. makeRect is the path-bearing node type —
    // when pathD is present the engine rasterizes it via bakePathToCanvas.
    pathD: partial.pathD,
    pathViewBox: partial.pathViewBox,
    prevX: 0, prevY: 0, prevRotation: 0, lastT: 0,
  };
  initPrev(node);
  return node;
}

export function makeImage(
  partial: Partial<SceneNode> & {
    x: number; y: number; w: number; h: number; imageUrl: string;
    radius?: number;
  },
): SceneNode {
  const id = partial.id ?? allocId();
  const radii = partial.radii ?? radiiAll(partial.radius ?? 0);
  const node: SceneNode = {
    id,
    type: "image",
    zIndex: partial.zIndex ?? 0,
    stackingContext: partial.stackingContext ?? false,
    x: partial.x, y: partial.y, w: partial.w, h: partial.h,
    radii,
    padding: partial.padding ?? [0, 0, 0, 0],
    fill: partial.fill ?? { r: 1, g: 1, b: 1, a: 1 },
    imageUrl: partial.imageUrl,
    borderWidths: partial.borderWidths ?? [0, 0, 0, 0],
    borderColor: partial.borderColor ?? { r: 0, g: 0, b: 0, a: 1 },
    strokeWidth: partial.strokeWidth ?? 0,
    stroke: partial.stroke ?? { r: 0, g: 0, b: 0, a: 1 },
    shadowOffsetX: partial.shadowOffsetX ?? 0,
    shadowOffsetY: partial.shadowOffsetY ?? 0,
    shadowBlur: partial.shadowBlur ?? 0,
    shadowSpread: partial.shadowSpread ?? 0,
    shadowColor: partial.shadowColor ?? { r: 0, g: 0, b: 0, a: 0.5 },
    rotation: partial.rotation ?? 0,
    scaleX: partial.scaleX ?? 1,
    scaleY: partial.scaleY ?? 1,
    transformOriginX: partial.transformOriginX ?? 0.5,
    transformOriginY: partial.transformOriginY ?? 0.5,
    opacity: partial.opacity ?? 1,
    mixBlendMode: partial.mixBlendMode ?? "normal",
    visibility: partial.visibility ?? "visible",
    pointerEvents: partial.pointerEvents ?? "auto",
    overflow: partial.overflow ?? "visible",
    filter: defaults(partial),
    update: partial.update,
    tagName: partial.tagName,
    className: partial.className,
    styleAttr: partial.styleAttr,
    animation: partial.animation,
    clickScene: partial.clickScene,
    // Image + path fields. makeRect is the path-bearing node type —
    // when pathD is present the engine rasterizes it via bakePathToCanvas.
    pathD: partial.pathD,
    pathViewBox: partial.pathViewBox,
    prevX: 0, prevY: 0, prevRotation: 0, lastT: 0,
  };
  initPrev(node);
  return node;
}

export function makeGradient(
  partial: Partial<SceneNode> & {
    x: number; y: number; w: number; h: number;
    fill: RGBA; gradientEnd: RGBA; gradientAngle?: number;
    radius?: number;
  },
): SceneNode {
  const id = partial.id ?? allocId();
  const radii = partial.radii ?? radiiAll(partial.radius ?? 0);
  const node: SceneNode = {
    id,
    type: "rect",
    zIndex: partial.zIndex ?? 0,
    stackingContext: partial.stackingContext ?? false,
    x: partial.x, y: partial.y, w: partial.w, h: partial.h,
    radii,
    padding: partial.padding ?? [0, 0, 0, 0],
    fill: partial.fill,
    gradientEnd: partial.gradientEnd,
    gradientAngle: partial.gradientAngle,
    borderWidths: partial.borderWidths ?? [0, 0, 0, 0],
    borderColor: partial.borderColor ?? { r: 0, g: 0, b: 0, a: 1 },
    strokeWidth: partial.strokeWidth ?? 0,
    stroke: partial.stroke ?? { r: 0, g: 0, b: 0, a: 1 },
    shadowOffsetX: partial.shadowOffsetX ?? 0,
    shadowOffsetY: partial.shadowOffsetY ?? 0,
    shadowBlur: partial.shadowBlur ?? 0,
    shadowSpread: partial.shadowSpread ?? 0,
    shadowColor: partial.shadowColor ?? { r: 0, g: 0, b: 0, a: 0.5 },
    rotation: partial.rotation ?? 0,
    scaleX: partial.scaleX ?? 1,
    scaleY: partial.scaleY ?? 1,
    transformOriginX: partial.transformOriginX ?? 0.5,
    transformOriginY: partial.transformOriginY ?? 0.5,
    opacity: partial.opacity ?? 1,
    mixBlendMode: partial.mixBlendMode ?? "normal",
    visibility: partial.visibility ?? "visible",
    pointerEvents: partial.pointerEvents ?? "auto",
    overflow: partial.overflow ?? "visible",
    filter: defaults(partial),
    update: partial.update,
    tagName: partial.tagName,
    className: partial.className,
    styleAttr: partial.styleAttr,
    animation: partial.animation,
    clickScene: partial.clickScene,
    // Image + path fields. makeRect is the path-bearing node type —
    // when pathD is present the engine rasterizes it via bakePathToCanvas.
    pathD: partial.pathD,
    pathViewBox: partial.pathViewBox,
    prevX: 0, prevY: 0, prevRotation: 0, lastT: 0,
  };
  initPrev(node);
  return node;
}

export function addNode(scene: Scene, node: SceneNode): void {
  scene.nodes.set(node.id, node);
  scene.insertion.push(node.id);
  scene.sorted = null;
  scene.version++;
}

export function removeNode(scene: Scene, id: NodeId): void {
  scene.nodes.delete(id);
  const i = scene.insertion.indexOf(id);
  if (i >= 0) scene.insertion.splice(i, 1);
  scene.sorted = null;
  scene.version++;
}

export function clearScene(scene: Scene): void {
  scene.nodes.clear();
  scene.insertion.length = 0;
  scene.sorted = null;
  scene.styleRules.clear();
  scene.focusedNode = null;
  scene.hoveredNode = null;
  scene.version++;
}

export function mutateNode(scene: Scene, id: NodeId, fn: (n: SceneNode) => void): void {
  const n = scene.nodes.get(id);
  if (!n) return;
  fn(n);
  scene.version++;
}

export function bumpZ(scene: Scene, id: NodeId, z: number): void {
  const n = scene.nodes.get(id);
  if (!n) return;
  n.zIndex = z;
  scene.sorted = null;
  scene.version++;
}

export function sortedNodes(scene: Scene): SceneNode[] {
  if (scene.sorted) return scene.sorted;
  const arr = Array.from(scene.nodes.values());
  arr.sort((a, b) => a.zIndex - b.zIndex);
  scene.sorted = arr;
  return arr;
}

export function snapshotPrev(nodes: SceneNode[]): void {
  const now = performance.now();
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    n.prevX = n.x;
    n.prevY = n.y;
    n.prevRotation = n.rotation;
    n.lastT = now;
  }
}

/** Update the index of nodes that match the given state predicate.
 *  Used by the engine to rebuild :hover / :focus style resolution. */
export function setHovered(scene: Scene, id: NodeId | null): void {
  const prev = scene.hoveredNode;
  scene.hoveredNode = id;
  if (prev !== id) {
    // Re-resolve styles for both the previously hovered and newly hovered
    // nodes so the visual transition (e.g. :hover filter change) is
    // immediate. Each call mutates the node's properties in place; the
    // compiler picks them up on the next frame.
    if (prev !== null) {
      const prevNode = scene.nodes.get(prev);
      if (prevNode) resolveNodeStyleRules(scene, prevNode);
    }
    if (id !== null) {
      const nextNode = scene.nodes.get(id);
      if (nextNode) resolveNodeStyleRules(scene, nextNode);
    }
    scene.version++;
  }
}

export function setFocused(scene: Scene, id: NodeId | null): void {
  const prev = scene.focusedNode;
  scene.focusedNode = id;
  if (prev !== id) {
    if (prev !== null) {
      const prevNode = scene.nodes.get(prev);
      if (prevNode) resolveNodeStyleRules(scene, prevNode);
    }
    if (id !== null) {
      const nextNode = scene.nodes.get(id);
      if (nextNode) resolveNodeStyleRules(scene, nextNode);
    }
    scene.version++;
  }
}

/**
 * Agent G (hover wiring): re-apply the matching CSS rule(s) for a node's
 * current state (:hover, :focus, base). Walks scene.styleRules, finds all
 * rules that match this node (by tag+class+pseudo), and writes the
 * resulting declarations onto the node in place.
 *
 * Layers of rules:
 *   1. base rule (no pseudo) — applied first
 *   2. matching pseudo rules — applied on top, in declaration order
 *
 * Only the properties present in the rule are written; other node
 * properties are untouched. This lets the rule act as a delta on top of
 * whatever inline style / parent inheritance produced initially.
 */
export function resolveNodeStyleRules(scene: Scene, node: SceneNode): void {
  const tag = node.tagName ?? "";
  const classes = (node.className ?? "").split(/\s+/).filter(Boolean);
  const isHovered = scene.hoveredNode === node.id;
  const isFocused = scene.focusedNode === node.id;

  // Build key variants. Rules in scene.styleRules may have been registered
  // with or without a tag prefix (the .cta rule is stored as ".cta", not
  // "div.cta"). We try both forms so matching works regardless of whether
  // the CSS specified a tag.
  const classKey = classes.join(".");
  const candidatesWithTag = [`${tag}.${classKey}`, `${tag}`, ""];
  const candidatesWithoutTag = [`.${classKey}`, ``];

  // 1. Base rule (no pseudo).
  for (const key of candidatesWithTag) {
    const rule = scene.styleRules.get(key);
    if (rule) { applyDeclsToNode(node, rule); break; }
  }
  for (const key of candidatesWithoutTag) {
    const rule = scene.styleRules.get(key);
    if (rule) { applyDeclsToNode(node, rule); break; }
  }

  // 2. Pseudo rules in declaration order.
  if (isHovered) {
    for (const key of candidatesWithTag) {
      const rule = scene.styleRules.get(`${key}:hover`);
      if (rule) { applyDeclsToNode(node, rule); break; }
    }
    for (const key of candidatesWithoutTag) {
      const rule = scene.styleRules.get(`${key}:hover`);
      if (rule) { applyDeclsToNode(node, rule); break; }
    }
  }
  if (isFocused) {
    for (const key of candidatesWithTag) {
      const rule = scene.styleRules.get(`${key}:focus`);
      if (rule) { applyDeclsToNode(node, rule); break; }
    }
    for (const key of candidatesWithoutTag) {
      const rule = scene.styleRules.get(`${key}:focus`);
      if (rule) { applyDeclsToNode(node, rule); break; }
    }
  }
}

/**
 * Apply a partial ComputedStyle (a single CSS rule's declarations) onto a
 * SceneNode. Mutates only the properties present in `decls`.
 *
 * Note: `decls` may contain RAW STRINGS for filter/background/etc. (because
 * `parseStyleAttr` only tokenizes, while full parsing happens in cssIngest's
 * `applyDecls`). For Agent G's hover wiring we re-parse filter values here.
 */
function applyDeclsToNode(node: SceneNode, decls: Partial<ComputedStyle>): void {
  if (decls.fill) {
    node.fill = { r: decls.fill.r, g: decls.fill.g, b: decls.fill.b, a: decls.fill.a };
  }
  if (decls.opacity !== undefined) node.opacity = decls.opacity;
  if (decls.scaleX !== undefined) node.scaleX = decls.scaleX;
  if (decls.scaleY !== undefined) node.scaleY = decls.scaleY;
  if (decls.rotation !== undefined) node.rotation = decls.rotation;
  if (decls.radii) node.radii = [decls.radii[0], decls.radii[1], decls.radii[2], decls.radii[3]];
  if (decls.shadowOffsetX !== undefined) node.shadowOffsetX = decls.shadowOffsetX;
  if (decls.shadowOffsetY !== undefined) node.shadowOffsetY = decls.shadowOffsetY;
  if (decls.shadowBlur !== undefined) node.shadowBlur = decls.shadowBlur;
  if (decls.shadowSpread !== undefined) node.shadowSpread = decls.shadowSpread;
  if (decls.shadowColor) {
    node.shadowColor = {
      r: decls.shadowColor.r, g: decls.shadowColor.g,
      b: decls.shadowColor.b, a: decls.shadowColor.a,
    };
  }
  if (decls.borderWidths) {
    node.borderWidths = [decls.borderWidths[0], decls.borderWidths[1], decls.borderWidths[2], decls.borderWidths[3]];
  }
  if (decls.borderColor) {
    node.borderColor = {
      r: decls.borderColor.r, g: decls.borderColor.g,
      b: decls.borderColor.b, a: decls.borderColor.a,
    };
  }
  if (decls.strokeWidth !== undefined) node.strokeWidth = decls.strokeWidth;
  if (decls.stroke) {
    node.stroke = {
      r: decls.stroke.r, g: decls.stroke.g,
      b: decls.stroke.b, a: decls.stroke.a,
    };
  }
  if (decls.filter) {
    // Filter decls may be a parsed FilterChain (if applyDecls already ran) or
    // a raw string (from parseStyleAttr). Handle both cases.
    const f = decls.filter as unknown;
    if (typeof f === "string") {
      // Parse the raw CSS filter string into a FilterChain and merge with
      // the node's current filter so properties not in the rule are kept.
      const parsed = parseFilterString(f);
      node.filter = {
        blur: parsed.blur ?? node.filter.blur,
        brightness: parsed.brightness ?? node.filter.brightness,
        contrast: parsed.contrast ?? node.filter.contrast,
        saturate: parsed.saturate ?? node.filter.saturate,
        hueRotate: parsed.hueRotate ?? node.filter.hueRotate,
        invert: parsed.invert ?? node.filter.invert,
        backdropBlur: parsed.backdropBlur ?? node.filter.backdropBlur,
      };
    } else if (typeof f === "object" && f !== null) {
      // Already a parsed FilterChain (e.g. from applyDecls).
      const parsed = f as { blur?: number; brightness?: number; contrast?: number; saturate?: number; hueRotate?: number; invert?: number; backdropBlur?: number };
      node.filter = {
        blur: parsed.blur ?? node.filter.blur,
        brightness: parsed.brightness ?? node.filter.brightness,
        contrast: parsed.contrast ?? node.filter.contrast,
        saturate: parsed.saturate ?? node.filter.saturate,
        hueRotate: parsed.hueRotate ?? node.filter.hueRotate,
        invert: parsed.invert ?? node.filter.invert,
        backdropBlur: parsed.backdropBlur ?? node.filter.backdropBlur,
      };
    }
  }
  if (decls.gradientEnd) {
    node.gradientEnd = {
      r: decls.gradientEnd.r, g: decls.gradientEnd.g,
      b: decls.gradientEnd.b, a: decls.gradientEnd.a,
    };
  }
  if (decls.gradientAngle !== undefined) node.gradientAngle = decls.gradientAngle;
}

/**
 * Parse a CSS filter string like "blur(8px) brightness(1.25) saturate(1.3)"
 * into a partial FilterChain. Returns only the keys that were present in
 * the string; missing keys are undefined so the caller can merge onto the
 * existing filter state.
 */
function parseFilterString(s: string): {
  blur?: number; brightness?: number; contrast?: number;
  saturate?: number; hueRotate?: number; invert?: number; backdropBlur?: number;
} {
  const out: ReturnType<typeof parseFilterString> = {};
  const re = /(blur|brightness|contrast|saturate|hue-rotate|invert|grayscale|sepia)\s*\(\s*([^)]+)\s*\)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    const fn = m[1].toLowerCase();
    const arg = m[2].trim();
    const num = arg.endsWith("%") ? Number(arg.slice(0, -1)) / 100 : Number(arg);
    switch (fn) {
      case "blur": out.blur = num; break;
      case "brightness": out.brightness = num; break;
      case "contrast": out.contrast = num; break;
      case "saturate": out.saturate = num; break;
      case "hue-rotate": out.hueRotate = (num * Math.PI) / 180; break;
      case "invert": out.invert = num; break;
    }
  }
  return out;
}